import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        
        <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
              Privacy <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
        <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Last Updated */}
            <div className="mb-12 text-center">
              <p className="text-gray-400 text-sm">
                Last updated: October 3, 2025
              </p>
            </div>

            <div className="space-y-12">
              
              {/* Introduction */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Introduction</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    RF Digital ("we," "our," or "us") operates the Eddystone URL Shortener service (the "Service"). 
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                    you use our Service, in compliance with the Protection of Personal Information Act (POPIA) of South Africa.
                  </p>
                  <p>
                    By using our Service, you consent to the data practices described in this policy. If you do not 
                    agree with the practices described in this policy, please do not use our Service.
                  </p>
                </div>
              </section>

              {/* Information We Collect */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Information We Collect</h2>
                <div className="text-gray-300 space-y-6">
                  
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Personal Information</h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Email address (for account creation and communication)</li>
                      <li>Name (if provided during registration)</li>
                      <li>Payment information (processed securely through third-party providers)</li>
                      <li>API usage data and preferences</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Usage Information</h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>URLs you shorten and their click statistics</li>
                      <li>Device information (browser type, operating system)</li>
                      <li>IP addresses and geographic location data</li>
                      <li>Service usage patterns and analytics</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Automatically Collected Information</h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Log files and server data</li>
                      <li>Cookies and similar tracking technologies</li>
                      <li>Referrer information</li>
                      <li>Time and date of access</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* How We Use Information */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">How We Use Your Information</h2>
                <div className="text-gray-300">
                  <p className="mb-4">We use the information we collect for the following purposes:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide, operate, and maintain our Service</li>
                    <li>Process transactions and send related information</li>
                    <li>Send administrative information and service updates</li>
                    <li>Provide customer support and respond to inquiries</li>
                    <li>Generate analytics and improve our Service</li>
                    <li>Detect, prevent, and address technical issues and fraud</li>
                    <li>Comply with legal obligations under South African law</li>
                  </ul>
                </div>
              </section>

              {/* Information Sharing */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Information Sharing and Disclosure</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We do not sell, trade, or otherwise transfer your personal information to third parties without 
                    your consent, except in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>With service providers who assist in operating our Service (under strict confidentiality agreements)</li>
                    <li>When required by law or to comply with legal processes</li>
                    <li>To protect our rights, property, or safety, or that of our users</li>
                    <li>In connection with a business transfer or merger</li>
                    <li>With your explicit consent</li>
                  </ul>
                </div>
              </section>

              {/* Data Security */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Data Security</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We implement appropriate technical and organizational security measures to protect your personal 
                    information against unauthorized access, alteration, disclosure, or destruction, in accordance 
                    with POPIA requirements.
                  </p>
                  <p>
                    These measures include encryption, secure servers, access controls, and regular security assessments. 
                    However, no method of transmission over the internet is 100% secure, and we cannot guarantee 
                    absolute security.
                  </p>
                </div>
              </section>

              {/* Your Rights */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Your Rights Under POPIA</h2>
                <div className="text-gray-300 space-y-4">
                  <p>Under the Protection of Personal Information Act, you have the following rights:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Right to access your personal information</li>
                    <li>Right to correct or update your personal information</li>
                    <li>Right to delete your personal information</li>
                    <li>Right to object to processing of your personal information</li>
                    <li>Right to data portability</li>
                    <li>Right to lodge a complaint with the Information Regulator</li>
                  </ul>
                  <p className="mt-4">
                    To exercise these rights, please contact us at privacy@rfdigi.co.za. We will respond to your 
                    request within 30 days as required by law.
                  </p>
                </div>
              </section>

              {/* Data Retention */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Data Retention</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    We retain your personal information only for as long as necessary to fulfill the purposes outlined 
                    in this Privacy Policy, unless a longer retention period is required or permitted by law.
                  </p>
                  <p>
                    When we no longer need your personal information, we will securely delete or anonymize it in 
                    accordance with our data retention policies and applicable legal requirements.
                  </p>
                </div>
              </section>

              {/* International Transfers */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">International Data Transfers</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    Your information may be transferred to and processed in countries other than South Africa. 
                    We ensure that such transfers comply with POPIA requirements and that adequate safeguards 
                    are in place to protect your personal information.
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Us</h2>
                <div className="text-gray-300 space-y-4">
                  <p>
                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                  </p>
                  <div className="bg-gray-900/50 border border-gray-600 rounded-macos p-4">
                    <p><strong>RF Digital</strong></p>
                    <p>Email: privacy@rfdigi.co.za</p>
                    <p>Address: South Africa</p>
                    <p>Information Officer: As required under POPIA</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Back to Home */}
            <div className="mt-16 text-center">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos hover:from-primary-600 hover:to-orange-600 transition-all duration-300 shadow-macos"
              >
                Back to Home
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;